import axios from '@/api/axios';
import { gql } from '@/api/utils';
import { BILLING_INTERVAL_OPTIONS_FIELDS } from '@/api/services/billingIntervalOptions';

const orderFields = gql`
id
status
city {
  baseUrl
  title
  logo {
    original
  }
  noLicensing
  organization
  letterLogo {
    original
    imageAltText
  }
  taxRate
  taxCode
  taxNumber
  processingFeeLabel
  processingFeeTooltipCopy
  replacementTagProcessingFeeLabel
  replacementTagProcessingFeeTooltipCopy
  name
  safeAndHappyFund
  useCustomDonationAmounts
  roundUpMyOrder
  donationAmounts{
    amount
  }
  partner {
    name
    product {
      id
    }
    image {
      original
    }
  }
  paymentProcessor {
    processor
    publicKey
  }
  paymentScreen
  locale
  province
  country
  currency
  skipDonation
  donationSectionTitle
  donationSectionDescription
  donationSectionImage {
    original
  }
  customerService {
    phoneNumber
  }
  petOwnerOrderMessage
  showWalletCardProducts
  customDonationMinAmount
  customDonationMaxAmount
  donationCollectionStatement
}
petOwner {
  id
  primaryPhone
  pets {
    id
    name
    images {
      id
      original
    }
    tags {
      ... on PetTagProduct { id name }
      ... on DesignerTagProduct { id name }
    }
  }
  renewalPets: petsUpForRenewal {
    id
    name
    type
    activeLicence {
      expiry
      expiryDateTime
      name
      startDate
      renewalDate
      tempLicense
    }
  }
  membershipCtaPostPurchaseUrl
}
subtotal
shipping {
  type
  name
  description
  fee
}
hasShippableItems
hasPriorityTrackedShipping
eligibleForPriorityTrackedShipping
canDowngradeToRegularShipping
shippingFeeOptions {
  type
  name
  description
  fee
}
tax
processingFee
onlineProcessingReplacementTagFee
onlineProcessingFeeExcludingReplacementTag
discountAmount
discountAmountContext {
  discountAmount
  confirmationContext {
    success
  }
}
roundUpAmount
discount {
  code
  includeSaleItems
}
discountAppliedMessage
total
pets {
  id
}
groups {
  id
  pet {
    id
  }
  walletCardConfigurations {
    optedIn
    completed
    url
    orderItemBundle {
      badge {
        text
        color
        backgroundColor
      }
    }
  }
  freePetTagContext {
    groupNotificationText
    containsFreePetTagWithReplacementFee  
  }
}
itemsCount
items {
  id
  discount
  automatedDiscountState
  automatedDiscountTriggerName
  subtotal
  total
  customization
  quantity
  isCompleted
  customTagOrderInfo {
    name
    phone
  }
  ${BILLING_INTERVAL_OPTIONS_FIELDS}
  addons {
    type
    title
    included
    retailPrice
    discountPrice
    savingAmount
    isDiscounted
  }
  parent {
    id
    automatedDiscountState
    product {
      id
      ... on DesignerTagProduct {
        style {
          id
          code
        }
      }
    }
  }
  product {
    id
    name
    type
    price
    type
    description
    image {
      original
    }
    ... on DesignerTagProduct {
      size
      color
      style {
        id
        code
      }
      municipalTag
      personalization {
        price
      }
      material {
        material
      }
      laserPrinterJig {
        shape
        material
      }
      keyChainPrice
      keyChainDiscountedPrice
      discountedPrice
    }
    ... on PetTagProduct {
      size
      municipalTag
      style {
        id
        code
      }
      discountedPrice
    }
    ... on SubscriptionProduct {
      productSubType
      subscriptionInterval
      subscriptionIntervalCount
    }
    ... on CollarProduct {
      size
      color
      backImage {
        original
      }
      personalization {
        price
      }
      style {
        id
        code
        productGroups {
          id
        }
      }
      discountedPrice
    }
    ... on LeashProduct {
      color
      length
      backImage {
        original
      }
      style {
        id
        code
        productGroups {
          id
        }
      }
      discountedPrice
    }
    ... on PetEssentialsProduct {
      status
      color
      size
      image {
        original
      }
      style {
        id
        code
        productGroups {
          id
        }
      }
    }  
    ... on AdditionalFeeProduct {
      subType
    }
    ... on LateFeeProduct {
      subType
    }
    ... on VoiceAndSightProduct {
      subType
    }
  }
  group {
    id
    pet {
      id
      name
      idCode
      licenses {
        id
       }
    }
  }
  children {
    id
    discount
    automatedDiscountState
    automatedDiscountTriggerName
    subtotal
    total
    orderItemBundle {
      id
    }
    group {
      pet {
        name
      }
    }
    parent {
      id
      automatedDiscountState
      product {
        id
        ... on DesignerTagProduct {
          style {
            id
            code
          }
        }
      }
    }
    product {
      id
      name
      type
      price
      description
      image {
        original
      }
      ... on DesignerTagProduct {
        keyChainPrice
        keyChainDiscountedPrice
      }
      ... on PetEssentialsProduct {
        sku
      }
    }
    keyChain
  }
  discount
  orderItemBundle {
    id
    badge {
      text
      color
      backgroundColor
    }
  }
}
ableToCheckout
prepareOrderForPaymentContext {
  removedItems
}
`;

const ORDER_UPDATED_EVENT = 'docupet:orderUpdated';

const dispatchOrderUpdatedEvent = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(ORDER_UPDATED_EVENT));
  }
};

export default {
  async getOrder(id) {
    try {
      const query = gql`
        query order($id: ID!) {
          order(id: $id) {
           ${orderFields}
          }
        }
      `;

      const variables = { id };
      const { order: results } = await axios.post(`/`, { query, variables });

      return results;
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    }
  },
  async applyDiscount(id, code) {
    try {
      const query = gql`
        mutation applyDiscount($id: ID! $code: String!){
          applyDiscount(order: $id, code: $code){
            ${orderFields}
          } 
        }
      `;

      const variables = { id, code };
      const { applyDiscount: results } = await axios.post(`/`, { query, variables });

      return results;
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    } finally {
      dispatchOrderUpdatedEvent();
    }
  },
  async removeDiscount(id) {
    try {
      const query = gql`
        mutation removeDiscount($id: ID!){
          removeDiscount(orderId: $id){
            ${orderFields}
          } 
        }
      `;

      const variables = { id };
      const { removeDiscount: results } = await axios.post(`/`, { query, variables });

      return results;
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    } finally {
      dispatchOrderUpdatedEvent();
    }
  },
  async orderDonation(id, amount) {
    try {
      const query = gql`
        mutation orderDonation($id: ID! $amount: Float!){
          orderDonation(orderId: $id, amount: $amount){
            ${orderFields}
          } 
        }
      `;

      const variables = { id, amount: parseFloat(amount) };
      const { orderDonation: results } = await axios.post(`/`, { query, variables });

      return results;
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    } finally {
      dispatchOrderUpdatedEvent();
    }
  },
  async pay(id, paymentMethod, amount, setDefaultPaymentMethod = false, autoRenewalConfig = null, marketingCommunicationOptIn = null) {
    try {
      const query = gql`
        mutation pay(
          $id: ID!,
          $paymentMethod: StripePaymentMethod,
          $amount: Float!,
          $setDefaultPaymentMethod: Boolean,
          $autoRenewalConfig: AutorenewalConfigInput,
          $marketingCommunicationOptIn: Boolean
         ){
          pay(
            orderId: $id,
            paymentMethod: $paymentMethod,
            amount: $amount,
            setDefaultPaymentMethod: $setDefaultPaymentMethod,
            autoRenewalConfig: $autoRenewalConfig,
            marketingCommunicationOptIn: $marketingCommunicationOptIn
          ){
            ${orderFields}
          } 
        }
      `;

      const variables = {
        id,
        amount,
        setDefaultPaymentMethod,
        autoRenewalConfig,
        marketingCommunicationOptIn,
        paymentMethod,
      };
      const { pay: results } = await axios.post(`/`, { query, variables });
      return results;
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    } finally {
      dispatchOrderUpdatedEvent();
    }
  },
  async removeItem(orderId, itemId) {
    try {
      const query = gql`
        mutation removeItem($orderId: ID!, $itemId: ID!){
          removeItem(orderId: $orderId, itemId: $itemId){
            ${orderFields}
          } 
        }
      `;

      const variables = { orderId, itemId };
      const { removeItem: results } = await axios.post(`/`, { query, variables });

      return results;
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    } finally {
      dispatchOrderUpdatedEvent();
    }
  },
  async changeMembershipBillingInterval(orderId, itemId, billingInterval) {
    try {
      const query = gql`
        mutation changeMembershipBillingInterval($orderId: ID!, $itemId: ID!, $billingInterval: MembershipBillingInterval!){
          changeMembershipBillingInterval(orderId: $orderId, itemId: $itemId, billingInterval: $billingInterval){
            ${orderFields}
          } 
        }
      `;

      const variables = { orderId, itemId, billingInterval };
      const { changeMembershipBillingInterval: results } = await axios.post(`/`, { query, variables });

      return results;
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    } finally {
      dispatchOrderUpdatedEvent();
    }
  },
  async removePersonalization(orderId, itemId) {
    try {
      const query = gql`
        mutation removePersonalization($orderId: ID!, $itemId: ID!){
          removePersonalization(orderId: $orderId, itemId: $itemId){
            ${orderFields}
          } 
        }
      `;

      const variables = { orderId, itemId };
      const { removePersonalization: results } = await axios.post(`/`, { query, variables });

      return results;
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    } finally {
      dispatchOrderUpdatedEvent();
    }
  },
  async addItem(orderId, productDetails) {
    try {
      const query = gql`
        mutation AddItemMutation($orderId: ID!, $productDetails: [OrderProductInput!]) {
          addItem(orderId: $orderId, products: $productDetails) {
            ${orderFields}
          }
        }
      `;

      const variables = { orderId, productDetails: [productDetails] };
      const { addItem: results } = await axios.post(`/`, { query, variables });

      return results;
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    } finally {
      dispatchOrderUpdatedEvent();
    }
  },
  async addPersonalization(orderId, { personalization, itemId }) {
    try {
      const query = gql`
        mutation addPersonalization($orderId: ID!, $itemId: ID!, $personalization: PersonalizationInput!) {
          addPersonalization(orderId: $orderId, itemId: $itemId, personalization: $personalization) {
            ${orderFields}
          }
        }
      `;

      const variables = { orderId, itemId, personalization };
      const { addPersonalization: results } = await axios.post(`/`, { query, variables });

      return results;
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    } finally {
      dispatchOrderUpdatedEvent();
    }
  },
  async temporaryOrder(cityId, products, discountCode) {
    try {
      const query = gql`
        mutation ($cityId: ID!, $products: [OrderProductInput]!, $discountCode: String) {
          temporaryOrder(cityId: $cityId, products: $products, discountCode: $discountCode) {
            status
            city {
              baseUrl
              title
              logo {
                original
              }
              taxRate
              taxCode
              taxNumber
              name
              locale
              province
              country
              customerService {
                phoneNumber
              }
              skipDonation
            }
            subtotal
            shipping {
              type
              name
              description
              fee
            }
             tax
             onlineProcessingFeeExcludingReplacementTag
             onlineProcessingReplacementTagFee
             discountAmount
            discount {
              code
              includeSaleItems
            }
            total
            itemsCount
            hasShippableItems
            hasPriorityTrackedShipping
            eligibleForPriorityTrackedShipping
            canDowngradeToRegularShipping
            items {
              discount
              automatedDiscountState
              automatedDiscountTriggerName
              subtotal
              total
              customization
              customTagOrderInfo {
                name
                phone
              }
              product {
                id
                name
                type
                price
                image {
                  original
                }
                ... on DesignerTagProduct {
                  size
                  color
                  style {
                    id
                    code
                  }
                  municipalTag
                  personalization {
                    price
                  }
                  laserPrinterJig {
                    shape
                    material
                  }
                  keyChainPrice
                  keyChainDiscountedPrice
                  discountedPrice
                }
                ... on PetTagProduct {
                  size
                  municipalTag
                  style {
                    id
                    code
                  }
                  discountedPrice
                }
                ... on CollarProduct {
                  size
                  color
                  backImage {
                    original
                  }
                  personalization {
                    price
                  }
                  style {
                    id
                    code
                    productGroups {
                      id
                    }
                  }
                  discountedPrice
                }
                ... on LeashProduct {
                  color
                  length
                  backImage {
                    original
                  }
                  style {
                    id
                    code
                    productGroups {
                      id
                    }
                  }
                  discountedPrice
                }
                ... on PetEssentialsProduct {
                  status
                  color
                  size
                  image {
                    original
                  }
                  style {
                    id
                    code
                    productGroups {
                      id
                    }
                  }
                }
                ... on AdditionalFeeProduct {
                  subType
                }
                ... on LateFeeProduct {
                  subType
                }
                ... on SubscriptionProduct {
                  productSubType
                }
              }
              parent {
                total
                automatedDiscountState
                product {
                  id
                  ... on DesignerTagProduct {
                    style {
                      id
                      code
                    }
                  }
                }
              }
              children {
                discount
                automatedDiscountState
                automatedDiscountTriggerName
                subtotal
                total
                parent {
                  total
                  automatedDiscountState
                  product {
                    id
                    ... on DesignerTagProduct {
                      style {
                        id
                        code
                      }
                    }
                  }
                  customTagOrderInfo {
                    name
                    phone
                  }
                }
                product {
                  id
                  name
                  type
                  price
                  image {
                    original
                  }
                  ... on DesignerTagProduct {
                    keyChainPrice
                    keyChainDiscountedPrice
                  }
                  ... on PetEssentialsProduct {
                    sku
                  }
                }
                keyChain
              }
              discount
              keyChain
            }
          }
        }
      `;
      const variables = { cityId, products, discountCode };
      const { temporaryOrder: results } = await axios.post(`/`, { query, variables });

      return results;
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    } finally {
      dispatchOrderUpdatedEvent();
    }
  },
  async cancelOrder(orderId) {
    try {
      const query = gql`
        mutation CancelOrderMutation($orderId: ID!) {
          cancelOrder(orderId: $orderId){
            ${orderFields}
          } 
        }
      `;

      const variables = { orderId };
      const { cancelOrder: results } = await axios.post(`/`, { query, variables });

      return results;
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    } finally {
      dispatchOrderUpdatedEvent();
    }
  },
  async upgradeToPriorityShipping(orderId) {
    try {
      const query = gql`
        mutation UpgradeToPriorityTrackedShippingMutation($orderId: ID!) {
          addPriorityTrackedShipping(orderId: $orderId){
            ${orderFields}
          } 
        }
      `;

      const { addPriorityTrackedShipping: results } = await axios.post(`/`, { query, variables: { orderId } });

      return Promise.resolve(results);
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    } finally {
      dispatchOrderUpdatedEvent();
    }
  },
  async downgradeToRegularShipping(orderId) {
    try {
      const query = gql`
        mutation DowngradeToRegularMailShippingMutation($orderId: ID!) {
          removePriorityTrackedShipping(orderId: $orderId){
            ${orderFields}
          } 
        }
      `;

      const { removePriorityTrackedShipping: results } = await axios.post(`/`, { query, variables: { orderId } });

      return Promise.resolve(results);
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    } finally {
      dispatchOrderUpdatedEvent();
    }
  },
  async removeItemsByProduct(orderId, productId, quantity) {
    try {
      const query = gql`
        mutation removeItemsByProduct($orderId: ID!, $productId: ID!, $quantity: Float!) {
          removeItemsByProduct(orderId: $orderId, productId: $productId, quantity: $quantity) {
            ${orderFields}
          }
        }
      `;

      const variables = { orderId, productId, quantity: Number(quantity) };
      const { removeItemsByProduct: results } = await axios.post(`/`, { query, variables });

      return Promise.resolve(results);
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    } finally {
      dispatchOrderUpdatedEvent();
    }
  },
  async removeWalletCard(input) {
    try {
      const query = gql`
        mutation updateOrderForPet($input: UpdateOrderForPetInput!) {
          updateOrderForPet(input: $input) {
            ${orderFields}
          }
        }
      `;
      const variables = {
        input,
      };
      const { updateOrderForPet: results } = await axios.post(`/`, { query, variables });

      return Promise.resolve(results);
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    } finally {
      dispatchOrderUpdatedEvent();
    }
  },
  async prepareOrderForPayment(input) {
    try {
      const query = gql`
        mutation prepareOrderForPayment($input: PrepareOrderForPaymentInput!) {
          prepareOrderForPayment(input: $input) {
            ${orderFields}
          }
        }
      `;
      const variables = {
        input,
      };
      const { prepareOrderForPayment: results } = await axios.post(`/`, { query, variables });

      return results;
    } catch (error) {
      throw new Error(error?.message);
    } finally {
      dispatchOrderUpdatedEvent();
    }
  },
};
